import React, { useEffect } from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import HeroSection from "./sections/HeroSection";
import NextGenReportingSection from "./sections/NextGenReportingSection";
import SmartReportVsVizAppBanner from "./sections/SmartReportVsVizAppBanner";
import ProductsGridSection from "./sections/ProductsGridSection";
import StatisticsSection from "./sections/StatisticsSection";
import NewLaunchesSection from "./sections/NewLaunchesSection";
import "./smartReportingPage.css";

const SmartReportingPage = () => {
  const theme = useTheme();

  // ── Clarity Event Tracking ──────────────────────────────────────────────
  useEffect(() => {
    const handleClick = (e) => {
      if (typeof window.clarity !== 'function') return;

      const el = e.target.closest('button, [role="button"], [class*="Mui"], [class*="css-"], div') || e.target;
      if (!el) return;
      const text = el.innerText?.trim().toLowerCase() || '';

      // 1. Learn More (Hero CTA)
      if (text.includes('learn more')) {
        window.clarity('set', 'cta_clicked', 'sr_learn_more');
        window.clarity('event', 'sr_learn_more_clicked');
      }

      // 2 & 3. Preview buttons — Smart Report vs Viz App (differentiated by card heading)
      if (text.includes('preview') && !text.includes('preview report')) {
        const card = el.closest('div[class*="MuiCard"], div[class*="css-"], div');
        const heading = card?.querySelector('h2, h3, h4, p, [class*="Typography"]')?.innerText?.trim().toLowerCase() || '';
        if (heading.includes('smart report')) {
          window.clarity('set', 'sr_nav_clicked', 'smart_report_subpage');
          window.clarity('event', 'sr_preview_smart_report_clicked');
        } else if (heading.includes('viz app')) {
          window.clarity('set', 'sr_nav_clicked', 'viz_app_subpage');
          window.clarity('event', 'sr_preview_viz_app_clicked');
        }
      }

      // 4. Visit Website (Lab Report Analyzer — external link button)
      // Note: The UI renders "Visit Website", but the doc says "Visit Feature". Supporting both.
      if (text.includes('visit website') || text.includes('visit feature')) {
        window.clarity('set', 'sr_new_launch_clicked', 'lab_report_analyzer');
        window.clarity('event', 'sr_launch_lab_analyzer_clicked');
      }

      // 5, 6, 7. Preview Report buttons (Allergy, Food Intolerance, Pre Natal)
      if (text.includes('preview report')) {
        const card = el.closest('div[class*="MuiCard"], div[class*="css-"], div[class*="launch"], div');
        const heading = card?.querySelector('h2, h3, h4, p, [class*="Typography"]')?.innerText?.trim().toLowerCase() || '';
        if (heading.includes('allergy')) {
          window.clarity('set', 'sr_new_launch_clicked', 'allergy_report');
          window.clarity('event', 'sr_launch_allergy_clicked');
        } else if (heading.includes('food intolerance')) {
          window.clarity('set', 'sr_new_launch_clicked', 'food_intolerance_report');
          window.clarity('event', 'sr_launch_food_intolerance_clicked');
        } else if (heading.includes('pre natal')) {
          window.clarity('set', 'sr_new_launch_clicked', 'pre_natal_report');
          window.clarity('event', 'sr_launch_pre_natal_clicked');
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);
  // ────────────────────────────────────────────────────────────────────────
  
  // Use SVG from public folder (same pattern as other pages)
  const backgroundSvgPath = "/images/userJourney/assets/job297-ploy-07c-blue-01.svg";

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "transparent",
        backgroundImage: `url(${backgroundSvgPath})`,
        backgroundSize: "cover",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "scroll",
      }}
    >
      {/* Content wrapper to ensure all sections appear above background */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
        }}
      >
        {/* Hero Section */}
        <HeroSection />
        
        {/* Next-Generation Smart Reporting Section */}
        <NextGenReportingSection />
        
        {/* Smart Report vs Viz App Banner */}
        <SmartReportVsVizAppBanner />
        
        {/* Products Grid Section - 3 cards left, 1 card right */}
        <ProductsGridSection />
        
        {/* Statistics Section */}
        <StatisticsSection />
        
        {/* New Launches Section */}
        <NewLaunchesSection />
      </Box>
    </Box>
  );
};

export default SmartReportingPage;

